import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ArrowUp, ArrowDown, Search, ExternalLink } from "lucide-react";
import { API } from "@/config/api";

const STATUS_DOT = { green: "#10b981", yellow: "#f59e0b", blue: "#2563eb", red: "#dc2626" };

const cellValue = (row, column) => {
  const raw = row[column.key];
  if (column.type === "bool") return raw ? "Yes" : "No";
  if (raw === null || raw === undefined || raw === "") return "—";
  return raw;
};

/** One table for every dashboard drill-down — the backend sends the columns with the rows. */
export const DrilldownModal = ({ open, onClose, view, stage, pipeline, initialFilter }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [term, setTerm] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!open || !view) return;
    setLoading(true);
    setData(null);
    setTerm("");
    setFilter(initialFilter || "all");
    setSort({ key: null, dir: "asc" });
    const params = { view };
    if (stage) params.stage = stage;
    if (pipeline) params.pipeline = pipeline;
    axios
      .get(`${API}/dashboard/manager/drilldown`, { params, withCredentials: true })
      .then((res) => setData(res.data))
      .catch(() => setData({ title: "Could not load this view", columns: [], rows: [] }))
      .finally(() => setLoading(false));
  }, [open, view, stage, pipeline, initialFilter]);

  const filterValues = useMemo(() => {
    if (!data?.filter_key) return [];
    return [...new Set((data.rows || []).map((row) => row[data.filter_key]).filter(Boolean))];
  }, [data]);

  const rows = useMemo(() => {
    let out = [...(data?.rows || [])];
    if (data?.filter_key && filter !== "all") out = out.filter((row) => row[data.filter_key] === filter);
    if (term.trim()) {
      const needle = term.trim().toLowerCase();
      out = out.filter((row) =>
        Object.values(row).some((value) => String(value ?? "").toLowerCase().includes(needle))
      );
    }
    if (sort.key) {
      out.sort((a, b) => {
        const x = a[sort.key] ?? "";
        const y = b[sort.key] ?? "";
        const result = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y));
        return sort.dir === "asc" ? result : -result;
      });
    }
    return out;
  }, [data, filter, term, sort]);

  const openRow = (row) => {
    if (data?.link === "case" && row.tin_number) {
      onClose();
      navigate(`/cases/${row.tin_number}`);
    } else if (data?.link === "doctor" && row.id) {
      onClose();
      navigate(`/doctor-timeline?doctor=${encodeURIComponent(row.id)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="drilldown-modal">
        <DialogHeader>
          <DialogTitle className="text-base pr-6" data-testid="drilldown-title">
            {loading ? "Loading…" : data?.title}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="overflow-y-auto space-y-3">
            {data?.note && <p className="text-xs text-amber-700 dark:text-amber-300">{data.note}</p>}

            {data?.funnel && (
              <div className="space-y-2" data-testid="drilldown-funnel">
                {data.funnel.map((step) => (
                  <div key={step.label} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span>{step.label}</span>
                      <span className="font-semibold">
                        {step.value} · {step.percent}%
                      </span>
                    </div>
                    <div className="h-2 rounded bg-muted overflow-hidden">
                      <div className="h-full bg-blue-600" style={{ width: `${Math.min(step.percent, 100)}%` }} />
                    </div>
                  </div>
                ))}
                {data.gap && (
                  <p className="text-xs text-muted-foreground pt-1">
                    {data.gap.still_in_titration} diagnosed patients are still working through titration ·{" "}
                    {data.gap.lost_in_titration} lost ·{" "}
                    {data.gap.avg_days_ss_report_to_done === null
                      ? "no completed titrations yet to measure the gap"
                      : `${data.gap.avg_days_ss_report_to_done} days on average from report to therapy`}
                  </p>
                )}
              </div>
            )}

            {data?.totals?.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="drilldown-totals">
                {data.totals.map((total) => (
                  <Badge key={total.label} variant="outline" className="text-[11px]">
                    {total.label}: {total.value}
                  </Badge>
                ))}
              </div>
            )}

            {data?.rows_label && <p className="text-sm font-medium pt-1">{data.rows_label}</p>}

            {(data?.rows?.length > 0 || term) && (
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={term}
                    onChange={(event) => setTerm(event.target.value)}
                    placeholder="Search…"
                    className="h-9 pl-8 text-sm"
                    data-testid="drilldown-search"
                  />
                </div>
                {filterValues.length > 1 && (
                  <div className="flex flex-wrap gap-1">
                    {["all", ...filterValues].map((value) => (
                      <Button
                        key={value}
                        size="sm"
                        variant={filter === value ? "default" : "outline"}
                        onClick={() => setFilter(value)}
                        className="h-8 text-xs capitalize"
                        data-testid={`drilldown-filter-${String(value).toLowerCase()}`}
                      >
                        {value}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground py-10 text-center" data-testid="drilldown-empty">
                Nothing to show here yet.
              </p>
            ) : (
              <div className="overflow-x-auto border rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60">
                    <tr>
                      {data.columns.map((column) => (
                        <th
                          key={column.key}
                          onClick={() =>
                            setSort((prev) => ({
                              key: column.key,
                              dir: prev.key === column.key && prev.dir === "asc" ? "desc" : "asc",
                            }))
                          }
                          className="px-3 py-2 text-left font-medium whitespace-nowrap cursor-pointer select-none"
                          data-testid={`drilldown-sort-${column.key}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {column.label}
                            {sort.key === column.key &&
                              (sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                          </span>
                        </th>
                      ))}
                      {data.link && <th className="w-8" />}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr
                        key={row.id || row.tin_number || index}
                        onClick={() => openRow(row)}
                        className={`border-t ${data.link ? "cursor-pointer hover:bg-muted/50" : ""}`}
                        data-testid={`drilldown-row-${row.id || row.tin_number || index}`}
                      >
                        {data.columns.map((column) => (
                          <td key={column.key} className="px-3 py-2 whitespace-nowrap">
                            {column.type === "status" ? (
                              <span
                                className="inline-block w-2.5 h-2.5 rounded-full"
                                style={{ background: STATUS_DOT[row[column.key]] || "#94a3b8" }}
                                aria-label={row[column.key]}
                              />
                            ) : (
                              cellValue(row, column)
                            )}
                          </td>
                        ))}
                        {data.link && (
                          <td className="px-2">
                            <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DrilldownModal;
